<script setup>
const props = defineProps({
    date: String,
    size: {
        type: String,
        default: 'small',
        validator: (value) => ['small', 'medium', 'large', 'xl'].includes(value)
    }
});

const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-14 h-14',
    xl: 'w-24 h-24'
};

const svgSizes = {
    small: { width: 24, height: 24 },
    medium: { width: 32, height: 32 },
    large: { width: 42, height: 42 },
    xl: { width: 64, height: 64 }
};

const textStyles = {
    small: {
        month: { top: '5px', fontSize: '5px' },
        day: { top: '12px', fontSize: '10px' }
    },
    medium: {
        month: { top: '10px', fontSize: '6px' },
        day: { top: '19px', fontSize: '13px' }
    },
    large: {
        month: { top: '11px', fontSize: '9px' },
        day: { top: '21px', fontSize: '18px' }
    },
    xl: {
        month: { top: '20px', fontSize: '13px' },
        day: { top: '38px', fontSize: '24px' }
    }
};

const containerClass = computed(() => sizeClasses[props.size]);
const svgSize = computed(() => svgSizes[props.size]);
const monthStyle = computed(() => textStyles[props.size].month);
const dayStyle = computed(() => textStyles[props.size].day);

// Parse the date
const parsedDate = computed(() => {
    if (!props.date) return { month: 'Jan', day: '1' };

    const date = new Date(props.date);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return {
        month: monthNames[date.getMonth()],
        day: date.getDate().toString()
    };
});
</script>

<template>
    <div :class="[containerClass, 'bg-gray-200 rounded-lg relative', props.size === 'xl' ? 'p-4' : props.size === 'large' ? 'p-2' : props.size === 'medium' ? 'p-2' : 'p-1']">
        <svg :width="svgSize.width" :height="svgSize.height" viewBox="0 0 64 62" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_890_6300)">
                <path d="M8.81934 6.59033C8.81934 8.09652 10.0407 9.3178 11.5469 9.31787H12.7969C14.3031 9.31787 15.5244 8.09656 15.5244 6.59033V4.14697H49.7295V6.59033C49.7295 8.09645 50.95 9.3177 52.4561 9.31787H53.7061C55.2123 9.31787 56.4336 8.09656 56.4336 6.59033V4.14697H56.8008C59.4517 4.14697 61.6005 6.29586 61.6006 8.94678V53.8921C61.6006 56.5431 59.4517 58.6919 56.8008 58.6919H6.40137C3.7504 58.6919 1.60059 56.5431 1.60059 53.8921V8.94678C1.60064 6.29586 3.75043 4.14697 6.40137 4.14697H8.81934V6.59033ZM55.0703 6.59033C55.0703 7.22185 54.6403 7.75165 54.0576 7.90674C54.6396 7.75129 55.0692 7.22223 55.0693 6.59131V4.14697H55.0703V6.59033ZM14.1602 6.59033C14.1602 7.2193 13.7344 7.7473 13.1553 7.90479C13.7336 7.74694 14.159 7.21968 14.1592 6.59131V4.14697H14.1602V6.59033Z" fill="white"/>
            </g>
            <path d="M10.1816 1.36364C10.1816 0.610522 10.7922 0 11.5453 0H12.7953C13.5484 0 14.1589 0.610521 14.1589 1.36364V6.59091C14.1589 7.34403 13.5484 7.95455 12.7953 7.95455H11.5453C10.7922 7.95455 10.1816 7.34403 10.1816 6.59091V1.36364Z" fill="#0E2140"/>
            <path d="M51.0908 1.36364C51.0908 0.610522 51.7013 0 52.4545 0H53.7045C54.4576 0 55.0681 0.610521 55.0681 1.36364V6.59091C55.0681 7.34403 54.4576 7.95455 53.7045 7.95455H52.4545C51.7013 7.95455 51.0908 7.34403 51.0908 6.59091V1.36364Z" fill="#0E2140"/>
            <path d="M8.81934 6.59033C8.81934 8.09652 10.0407 9.3178 11.5469 9.31787H12.7969C14.3031 9.31787 15.5244 8.09656 15.5244 6.59033V4.14697H49.7295V6.59033C49.7295 8.09645 50.95 9.3177 52.4561 9.31787H53.7061C55.2123 9.31787 56.4336 8.09656 56.4336 6.59033V4.14697H56.8008C59.4517 4.14697 61.6005 6.29586 61.6006 8.94678V20.7944H1.60059V8.94678C1.60064 6.29586 3.75043 4.14697 6.40137 4.14697H8.81934V6.59033ZM55.0703 6.59033C55.0703 7.22185 54.6403 7.75165 54.0576 7.90674C54.6396 7.75129 55.0692 7.22223 55.0693 6.59131V4.14697H55.0703V6.59033ZM14.1602 6.59033C14.1602 7.2193 13.7344 7.7473 13.1553 7.90479C13.7336 7.74694 14.159 7.21968 14.1592 6.59131V4.14697H14.1602V6.59033Z" fill="#CE2727"/>
            <defs>
                <filter id="filter0_d_890_6300" x="0.000585794" y="4.14697" width="63.2" height="57.7449" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="1.6"/>
                    <feGaussianBlur stdDeviation="0.8"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0.823529 0 0 0 0 0.866667 0 0 0 0 0.92549 0 0 0 1 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_890_6300"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_890_6300" result="shape"/>
                </filter>
            </defs>
        </svg>
        <div class="absolute left-0 right-0 text-white text-center uppercase font-medium" :style="{ top: monthStyle.top, fontSize: monthStyle.fontSize }">
            {{ parsedDate.month }}
        </div>
        <div class="absolute left-0 right-0 font-bold text-center" :style="{ top: dayStyle.top, fontSize: dayStyle.fontSize }">
            {{ parsedDate.day }}
        </div>
    </div>
</template>
